import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import AdvancedFilter from '../components/AdvancedFilter';
import { ActionButton, ActionButtonGroup, ActionLink } from '../components/ActionControls';
import { useUser } from '../store/slice/userSlice';
import { approveVendor, selectVendorLoading } from '../store/slice/vendorSlice';
import moment from 'moment';

const PAGE_SIZE = 20;
const TABS = [
  { id: 'users', label: 'Users' },
  { id: 'vendors', label: 'Vendors' },
];

function formatRole(role) {
  if (!role) return '—';
  const normalized = String(role);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

function isActiveUser(user) {
  if (user?.status) return user.status === 'Active';
  return user?.is_active === true || user?.is_active === 1 || user?.is_active === 'true';
}

function isApprovedUser(user) {
  return user?.is_approved === true || user?.is_approved === 1 || user?.is_approved === 'true';
}

function formatUserStatus(user) {
  if (user?.status) return user.status;
  return isActiveUser(user) ? 'Active' : 'Inactive';
}

function UsersPage() {
  const dispatch = useDispatch();
  const vendorActionLoading = useSelector(selectVendorLoading);
  const { users, loading, error, pagination, fetchUsers } = useUser();
  const [activeTab, setActiveTab] = useState('users');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    is_active: '',
    include_inactive: '0',
    is_approved: 'all',
    is_email_verified: 'all',
    auth_provider: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const isVendorsTab = activeTab === 'vendors';

  const apiFilters = useMemo(() => {
    const body = {
      page,
      limit: PAGE_SIZE,
    };

    if (filters.search.trim()) body.search = filters.search.trim();
    if (isVendorsTab) {
      body.role = 'vendor';
    } else if (filters.role && filters.role !== 'all') {
      body.role = filters.role;
    }
    if (filters.is_approved && filters.is_approved !== 'all') body.is_approved = filters.is_approved;
    if (filters.is_email_verified && filters.is_email_verified !== 'all') {
      body.is_email_verified = filters.is_email_verified;
    }
    if (filters.auth_provider && filters.auth_provider !== 'all') {
      body.auth_provider = filters.auth_provider;
    }
    if (filters.dateFrom) body.from_date = filters.dateFrom;
    if (filters.dateTo) body.to_date = filters.dateTo;

    if (filters.include_inactive === '1') {
      body.include_inactive = 'true';
    } else if (filters.is_active === 'false') {
      body.is_active = 'false';
    } else {
      body.is_active = 'true';
    }

    return body;
  }, [filters, page, isVendorsTab]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    fetchUsers(apiFilters);
  }, [apiFilters, fetchUsers]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
    if (tabId === 'users') {
      setFilters((prev) => (prev.role === 'vendor' ? { ...prev, role: 'all' } : prev));
    }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'dateRange') {
      setFilters((prev) => ({
        ...prev,
        dateFrom: value?.from || '',
        dateTo: value?.to || '',
      }));
    } else if (key === 'status') {
      if (value === 'active_only') {
        setFilters((prev) => ({ ...prev, is_active: 'true', include_inactive: '0' }));
      } else if (value === 'all') {
        setFilters((prev) => ({ ...prev, is_active: '', include_inactive: '1' }));
      } else {
        setFilters((prev) => ({ ...prev, is_active: value, include_inactive: '0' }));
      }
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
    setPage(1);
  };

  const handleApproveVendor = async (userId, name) => {
    const result = await dispatch(approveVendor(userId));
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success(`Approved vendor "${name}" successfully`);
      fetchUsers(apiFilters);
    }
  };

  const statusFilterValue =
    filters.include_inactive === '1' ? 'all' : filters.is_active || 'active_only';

  const userRoleFilter = {
    key: 'role',
    type: 'select',
    label: 'Role',
    value: filters.role,
    defaultValue: 'all',
    options: [
      { value: 'admin', label: 'Admin' },
      { value: 'stylist', label: 'Stylist' },
      { value: 'customer', label: 'Customer' },
      { value: 'user', label: 'User' },
    ],
  };

  const sharedFilters = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      value: statusFilterValue,
      defaultValue: 'active_only',
      options: [
        { value: 'active_only', label: 'Active only' },
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
        { value: 'all', label: 'All (incl. inactive)' },
      ],
    },
    {
      key: 'is_approved',
      type: 'select',
      label: 'Approved',
      value: filters.is_approved,
      defaultValue: 'all',
      options: [
        { value: 'true', label: 'Approved' },
        { value: 'false', label: 'Not approved' },
      ],
    },
    {
      key: 'is_email_verified',
      type: 'select',
      label: 'Email Verified',
      value: filters.is_email_verified,
      defaultValue: 'all',
      options: [
        { value: 'true', label: 'Verified' },
        { value: 'false', label: 'Not verified' },
      ],
    },
    {
      key: 'auth_provider',
      type: 'select',
      label: 'Auth Provider',
      value: filters.auth_provider,
      defaultValue: 'all',
      options: [
        { value: 'local', label: 'Local' },
        { value: 'google', label: 'Google' },
      ],
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      label: 'Joined Date',
      value: { from: filters.dateFrom, to: filters.dateTo },
    },
  ];

  const filterConfig = isVendorsTab
    ? sharedFilters
    : [sharedFilters[0], userRoleFilter, ...sharedFilters.slice(1)];

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (!isVendorsTab && filters.role && filters.role !== 'all') count++;
    if (filters.is_active || filters.include_inactive === '1') count++;
    if (filters.is_approved && filters.is_approved !== 'all') count++;
    if (filters.is_email_verified && filters.is_email_verified !== 'all') count++;
    if (filters.auth_provider && filters.auth_provider !== 'all') count++;
    if (filters.dateFrom || filters.dateTo) count++;
    return count;
  }, [filters, isVendorsTab]);

  const totalUsers = pagination.total || users.length;
  const currentPage = pagination.page || page;
  const totalPages = pagination.totalPages || 1;
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const approvedOnPage = users.filter(isApprovedUser).length;
  const pendingOnPage = users.length - approvedOnPage;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">
            {isVendorsTab ? 'Vendors' : 'Users'}
          </h1>
          <p className="mt-1 text-slate-500">
            {isVendorsTab
              ? 'Manage vendor accounts and approvals'
              : 'Manage your users and their roles'}
          </p>
        </div>
        {!isVendorsTab && (
          <Link
            to="/users/add"
            className="btn-primary inline-flex items-center gap-2"
          >
            <span>+</span> Add User
          </Link>
        )}
      </div>

      <div className="inline-flex rounded-premium border border-slate-200 bg-white p-1 shadow-sm">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabChange(tab.id)}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-amber-500 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">
            Total {isVendorsTab ? 'Vendors' : 'Users'}
          </p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{totalUsers}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">On This Page</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{users.length}</p>
        </div>
        {isVendorsTab ? (
          <>
            <div className="card-premium p-5">
              <p className="text-sm font-medium text-slate-500">Approved</p>
              <p className="mt-1 text-2xl font-bold text-emerald-600">{approvedOnPage}</p>
            </div>
            <div className="card-premium p-5">
              <p className="text-sm font-medium text-slate-500">Pending</p>
              <p className="mt-1 text-2xl font-bold text-slate-800">{pendingOnPage}</p>
            </div>
          </>
        ) : (
          <>
            <div className="card-premium p-5">
              <p className="text-sm font-medium text-slate-500">Admins</p>
              <p className="mt-1 text-2xl font-bold text-slate-800">
                {users.filter((u) => String(u.role).toLowerCase() === 'admin').length}
              </p>
            </div>
            <div className="card-premium p-5">
              <p className="text-sm font-medium text-slate-500">Customers</p>
              <p className="mt-1 text-2xl font-bold text-slate-800">
                {users.filter((u) => String(u.role).toLowerCase() === 'customer').length}
              </p>
            </div>
          </>
        )}
      </div>

      <AdvancedFilter
        searchPlaceholder={
          isVendorsTab
            ? 'Search vendors by name or email...'
            : 'Search users by name or email...'
        }
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        activeCount={activeFilterCount}
      />

      <div className="card-premium overflow-hidden">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {isVendorsTab ? 'Vendor' : 'User'}
                </th>
                {!isVendorsTab && (
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Role
                  </th>
                )}
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Status
                </th>
                {isVendorsTab && (
                  <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Approved
                  </th>
                )}
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Joined
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Auth Provider
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Phone
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Email Verified
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const role = formatRole(user.role);
                const status = formatUserStatus(user);
                const approved = isApprovedUser(user);
                const displayName = user.full_name || user.email || `User #${user.id}`;

                return (
                  <tr key={user.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-semibold">
                          {user?.profile_image_url ? (
                            <img src={user.profile_image_url} alt={displayName} className="w-10 h-10 rounded-full" />
                          ) : (
                            displayName[0] || '?'
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{displayName}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    {!isVendorsTab && (
                      <td className="py-4 px-6">
                        <span
                          className={`status-pill ${
                            role === 'Admin'
                              ? 'status-pill-purple'
                              : role === 'Stylist'
                              ? 'status-pill-info'
                              : 'status-pill-muted'
                          }`}
                        >
                          {role}
                        </span>
                      </td>
                    )}
                    <td className="py-4 px-6">
                      <span
                        className={`status-pill ${
                          status === 'Active' ? 'status-pill-success' : 'status-pill-muted'
                        }`}
                      >
                        {status}
                      </span>
                    </td>
                    {isVendorsTab && (
                      <td className="py-4 px-6">
                        <span
                          className={`status-pill ${
                            approved ? 'status-pill-success' : 'status-pill-muted'
                          }`}
                        >
                          {approved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                    )}
                    <td className="py-4 px-6 text-slate-600">
                      {user.created_at ? moment(user.created_at).format('DD/MM/YYYY') : '—'}
                    </td>
                    <td className="py-4 px-6 text-slate-600">{user.auth_provider || '—'}</td>
                    <td className="py-4 px-6 text-slate-600">{user.phone || '—'}</td>
                    <td className="py-4 px-6 text-slate-600">
                      {user.is_email_verified === true ||
                      user.is_email_verified === 1 ||
                      user.is_email_verified === 'true'
                        ? 'Yes'
                        : 'No'}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <ActionButtonGroup>
                        <ActionLink to={`/users/${user.id}/edit`} variant="edit">
                          Edit
                        </ActionLink>
                        {isVendorsTab && (
                          <ActionLink to={`/vendor-bank-accounts/user/${user.id}`} variant="neutral">
                            Bank Account
                          </ActionLink>
                        )}
                        {isVendorsTab && !approved && (
                          <ActionButton
                            type="button"
                            variant="success"
                            onClick={() => handleApproveVendor(user.id, displayName)}
                            disabled={vendorActionLoading}
                          >
                            Approve
                          </ActionButton>
                        )}
                      </ActionButtonGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {(loading && users.length === 0) && (
          <div className="py-16 text-center text-slate-500">
            Loading {isVendorsTab ? 'vendors' : 'users'}...
          </div>
        )}
        {!loading && users.length === 0 && (
          <div className="py-16 text-center text-slate-500">
            No {isVendorsTab ? 'vendors' : 'users'} found matching your criteria.
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-100 px-6 py-4">
            <p className="text-sm text-slate-500">
              Page {currentPage} of {totalPages} · {totalUsers} {isVendorsTab ? 'vendors' : 'users'} total
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={!canGoPrev || loading}
                className="px-4 py-2 rounded-premium border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={!canGoNext || loading}
                className="px-4 py-2 rounded-premium border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UsersPage;
